(function () {
  'use strict';

  const GA4_MEASUREMENT_ID = 'G-PKHVJ3LX7Z';

  function cleanParams(params) {
    return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));
  }

  function getCookie(name) {
    const prefix = `${name}=`;
    const item = document.cookie.split('; ').find((cookie) => cookie.indexOf(prefix) === 0);
    return item ? decodeURIComponent(item.slice(prefix.length)) : '';
  }

  function analyticsAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
    } catch (error) {
      return false;
    }
    return true;
  }

  function gaClientId() {
    const gaCookie = getCookie('_ga');
    const parts = gaCookie.split('.');
    if (parts.length >= 4) return parts.slice(-2).join('.');

    const storageKey = 'mma_size_guide_ga_cid';
    try {
      const stored = window.sessionStorage.getItem(storageKey);
      if (stored) return stored;
      const generated = `${Math.floor(Math.random() * 2147483647)}.${Math.floor(Date.now() / 1000)}`;
      window.sessionStorage.setItem(storageKey, generated);
      return generated;
    } catch (error) {
      return `${Math.floor(Math.random() * 2147483647)}.${Math.floor(Date.now() / 1000)}`;
    }
  }

  function gaSessionId() {
    const cookieName = `_ga_${GA4_MEASUREMENT_ID.replace('G-', '')}`;
    const match = getCookie(cookieName).match(/\$s(\d+)/);
    return match ? match[1] : String(Math.floor(Date.now() / 1000));
  }

  function sendGa4Event(name, payload) {
    if (!analyticsAllowed()) return;

    const query = new URLSearchParams({
      v: '2',
      tid: GA4_MEASUREMENT_ID,
      cid: gaClientId(),
      en: name,
      _p: String(Date.now()),
      dl: window.location.href,
      dt: document.title,
      sr: `${window.screen.width}x${window.screen.height}`,
      ul: (navigator.language || 'en-us').toLowerCase(),
      sid: gaSessionId(),
      sct: '1',
      seg: '1'
    });

    Object.entries(payload).forEach(([key, value]) => {
      const prefix = typeof value === 'number' ? 'epn' : 'ep';
      query.set(`${prefix}.${key}`, String(value));
    });

    const endpoint = `https://region1.analytics.google.com/g/collect?${query.toString()}`;
    let sent = false;
    try {
      sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint, '');
    } catch (error) {
      sent = false;
    }

    if (!sent && typeof window.fetch === 'function') {
      window.fetch(endpoint, { method: 'POST', mode: 'no-cors', keepalive: true }).catch(() => {});
    }
  }

  function sendEvent(name, params) {
    const payload = cleanParams(params);
    sendGa4Event(name, payload);

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (error) {
      // GA4 remains the canonical delivery path if the optional Shopify event bus is unavailable.
    }
  }

  function initGuide(modal) {
    if (modal.dataset.sizeGuideInitialized === 'true') return;
    modal.dataset.sizeGuideInitialized = 'true';

    const modalId = modal.id;
    const trigger = document.querySelector(`[href="#${modalId}"][data-size-guide-open]`);
    const dialog = modal.querySelector('[data-modal-dialog]');
    const scrollRoot = modal.querySelector('[data-size-guide-scroll]');
    const image = modal.querySelector('[data-size-guide-image]');
    const rows = Array.from(modal.querySelectorAll('[data-guide-row]'));
    const applyButton = modal.querySelector('[data-guide-apply]');
    const status = modal.querySelector('[data-guide-status]');
    const closeButton = modal.querySelector('[data-size-guide-close="button"]');
    const productRoot = modal.closest('.main-product') || document;

    // Keep the trigger inside the native Size label, but place the modal at the
    // document root so product and sticky-header stacking contexts cannot clip it.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    let selected = null;
    let openState = false;
    let openedAt = 0;
    let highestStep = 0;
    let applied = false;
    let pendingCloseReason = '';
    let stepObserver = null;
    let seenSteps = new Set();

    function pickerRoot() {
      return productRoot.querySelector('variant-radios, variant-selects');
    }

    function sizeControl() {
      const picker = pickerRoot();
      return picker ? picker.querySelector('[data-option-role="size"]') : null;
    }

    function currentSizeValue() {
      const control = sizeControl();
      if (!control) return '';
      const checked = control.querySelector('input:checked');
      if (checked) return checked.value;
      const select = control.querySelector('select');
      return select ? select.value : '';
    }

    function currentVariantId() {
      const picker = pickerRoot();
      const sectionId = picker && picker.dataset.section;
      const form = sectionId ? document.getElementById(`product-form-${sectionId}`) : null;
      const input = form && form.querySelector('input[name="id"]');
      return input ? input.value : '';
    }

    function commonParams(extra) {
      return Object.assign({
        source: 'pdp_size_guide',
        guide_type: modal.dataset.guideType,
        guide_version: modal.dataset.guideVersion,
        product_id: modal.dataset.productId,
        product_handle: modal.dataset.productHandle,
        product_title: modal.dataset.productTitle,
        product_brand: modal.dataset.productBrand,
        current_variant_id: currentVariantId(),
        page_path: window.location.pathname
      }, extra || {});
    }

    function findPickerOption(value) {
      const control = sizeControl();
      if (!control) return null;
      return Array.from(control.querySelectorAll('input, option')).find((option) => option.value === value) || null;
    }

    function rowIsUnavailable(row) {
      const option = findPickerOption(row.dataset.variantValue);
      if (!option) return true;
      return option.tagName === 'INPUT'
        ? option.classList.contains('disabled') || option.disabled
        : option.disabled || /unavailable/i.test(option.textContent || '');
    }

    function setStatus(message, isError) {
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(isError));
    }

    function clearSelection() {
      selected = null;
      rows.forEach((row) => {
        row.classList.remove('is-selected');
        const radio = row.querySelector('[data-guide-radio]');
        if (radio) radio.checked = false;
      });
      applyButton.disabled = true;
      applyButton.textContent = modal.dataset.guideType === 'boxing'
        ? 'Choose a weight'
        : modal.dataset.guideType === 'choice'
          ? 'Choose an option'
          : 'Choose a size';
      setStatus('Select a row.', false);
    }

    function selectRow(row, source) {
      if (!row || row.classList.contains('is-unavailable')) return;
      const previous = selected ? selected.display : '';
      const radio = row.querySelector('[data-guide-radio]');
      rows.forEach((item) => item.classList.toggle('is-selected', item === row));
      if (radio) radio.checked = true;
      selected = {
        display: row.dataset.size,
        variantValue: row.dataset.variantValue
      };
      applyButton.disabled = false;
      applyButton.textContent = `Use ${selected.display}`;
      setStatus(`${selected.display} selected.`, false);

      if (source === 'chart_row') {
        sendEvent('mma_size_guide_size_select', commonParams({
          selected_size: selected.display,
          selected_variant_value: selected.variantValue,
          previous_guide_size: previous,
          selection_source: 'chart_row'
        }));
      }
    }

    function syncRowsWithPicker() {
      rows.forEach((row) => {
        const unavailable = rowIsUnavailable(row);
        row.classList.toggle('is-unavailable', unavailable);
        row.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        const radio = row.querySelector('[data-guide-radio]');
        if (radio) radio.disabled = unavailable;
        if (unavailable && selected && selected.variantValue === row.dataset.variantValue) clearSelection();
      });
    }

    function preselectCurrentSize() {
      const value = currentSizeValue();
      const row = rows.find((item) => item.dataset.variantValue === value && !item.classList.contains('is-unavailable'));
      if (row) selectRow(row, 'preselected');
      else clearSelection();
      return value;
    }

    function beginStepTracking() {
      if (stepObserver) stepObserver.disconnect();
      seenSteps = new Set();
      highestStep = 0;

      stepObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
          const stepNumber = Number(entry.target.dataset.sizeGuideStep || 0);
          if (!stepNumber || seenSteps.has(stepNumber)) return;
          seenSteps.add(stepNumber);
          highestStep = Math.max(highestStep, stepNumber);
          sendEvent('mma_size_guide_step_view', commonParams({
            step_number: stepNumber,
            step_name: entry.target.dataset.stepName
          }));
          sendEvent(`mma_size_guide_step_${stepNumber}`, commonParams({
            step_name: entry.target.dataset.stepName
          }));
        });
      }, { root: scrollRoot, threshold: [0.35, 0.6] });

      modal.querySelectorAll('[data-size-guide-step]').forEach((step) => stepObserver.observe(step));
    }

    function openGuide() {
      if (openState) return;
      openState = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      openedAt = Date.now();
      applied = false;
      pendingCloseReason = '';
      document.documentElement.classList.add('mma-size-guide-is-open');
      if (image && !image.getAttribute('src')) image.setAttribute('src', image.dataset.src);
      syncRowsWithPicker();
      const preselected = preselectCurrentSize();
      if (scrollRoot) scrollRoot.scrollTop = 0;
      beginStepTracking();
      sendEvent('mma_size_guide_open', commonParams({
        trigger: 'size_selector_link',
        preselected_size: preselected
      }));
    }

    function recordClose(reason) {
      if (!openState) return;
      openState = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mma-size-guide-is-open');
      if (stepObserver) stepObserver.disconnect();
      sendEvent('mma_size_guide_close', commonParams({
        reason: pendingCloseReason || reason,
        selected_size: selected ? selected.display : '',
        applied: applied ? 'true' : 'false',
        highest_step: highestStep,
        dwell_time_ms: Math.max(0, Date.now() - openedAt)
      }));
      pendingCloseReason = '';
    }

    function applySize() {
      if (!selected) return;
      const control = sizeControl();
      const target = findPickerOption(selected.variantValue);
      const previousSize = currentSizeValue();
      const previousVariantId = currentVariantId();

      if (!control || !target) {
        setStatus('This size is not available on the product selector. Contact us before ordering.', true);
        return;
      }

      const unavailable = target.tagName === 'INPUT'
        ? target.classList.contains('disabled') || target.disabled
        : target.disabled || /unavailable/i.test(target.textContent || '');

      if (unavailable) {
        setStatus(`${selected.display} is unavailable in the selected color. Choose another color or another available size.`, true);
        syncRowsWithPicker();
        return;
      }

      if (target.tagName === 'INPUT') {
        // This theme binds the variant update through the visible swatch label.
        // Clicking the hidden radio directly can be reverted before the product
        // form updates, so use the same control path as a shopper.
        const label = Array.from(control.querySelectorAll('label')).find((item) => item.htmlFor === target.id);
        if (label) label.click();
        else target.click();
      } else {
        target.parentElement.value = target.value;
        target.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      }

      window.setTimeout(() => {
        const appliedSize = currentSizeValue();
        const appliedVariantId = currentVariantId();
        const success = appliedSize === selected.variantValue && Boolean(appliedVariantId);

        if (!success) {
          setStatus('The product selector did not update. Choose the size directly on the page.', true);
          sendEvent('mma_size_guide_apply', commonParams({
            selected_size: selected.display,
            previous_size: previousSize,
            previous_variant_id: previousVariantId,
            apply_result: 'failed'
          }));
          sendEvent('mma_size_guide_apply_failed', commonParams({
            selected_size: selected.display,
            previous_size: previousSize,
            previous_variant_id: previousVariantId
          }));
          return;
        }

        applied = true;
        sendEvent('mma_size_guide_apply', commonParams({
          selected_size: selected.display,
          selected_variant_value: selected.variantValue,
          previous_size: previousSize,
          previous_variant_id: previousVariantId,
          applied_variant_id: appliedVariantId,
          apply_result: 'success'
        }));
        sendEvent('mma_size_guide_apply_success', commonParams({
          selected_size: selected.display,
          selected_variant_value: selected.variantValue,
          previous_size: previousSize,
          previous_variant_id: previousVariantId,
          applied_variant_id: appliedVariantId
        }));
        pendingCloseReason = 'applied';
        if (closeButton) closeButton.click();
      }, 120);
    }

    if (trigger) trigger.addEventListener('click', openGuide);

    modal.addEventListener('click', (event) => {
      const apply = event.target.closest('[data-guide-apply]');
      if (apply) {
        event.preventDefault();
        applySize();
        return;
      }

      const close = event.target.closest('[data-size-guide-close]');
      if (close) recordClose(close.dataset.sizeGuideClose || 'button');

      const row = event.target.closest('[data-guide-row]');
      if (!row || row.classList.contains('is-unavailable')) return;
      if (event.target.matches('input')) return;
      const radio = row.querySelector('[data-guide-radio]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    modal.addEventListener('change', (event) => {
      if (!event.target.matches('[data-guide-radio]')) return;
      selectRow(event.target.closest('[data-guide-row]'), 'chart_row');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !openState || !modal.classList.contains('is-open')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pendingCloseReason = 'escape';
      if (closeButton) closeButton.click();
    }, true);

    if (dialog) {
      dialog.addEventListener('transitionend', () => {
        if (modal.classList.contains('is-open') && !openState) openGuide();
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-mma-size-guide]').forEach(initGuide);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
